import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import type { AffiliateRouterArm } from '~/src/lib/affiliateRouter'
import useAshleyFurnitureLane, {
  ASHLEY_LANE_PAUSED,
  useAshleyFurnitureLaneExposure,
} from '~/src/hooks/useAshleyFurnitureLane'

export interface AffiliateRouterArmState {
  arm: AffiliateRouterArm | null
  isReady: boolean
  routerEnabled: boolean
}

export interface AffiliateRouterBroadcastGate {
  canBroadcast: boolean
  timedOut: boolean
}

export interface AffiliateRouterBroadcastWiring {
  shouldBroadcast: boolean
  routerArm: AffiliateRouterArm | null
  routerEnabled: boolean
  arm: AffiliateRouterArm | null
  isReady: boolean
  timedOut: boolean
}

// How long the broadcast waits for the affiliate-router arm to resolve before
// giving up and falling back to the legacy Wayfair path. Bootstrapped sessions
// (the common case) resolve synchronously from the cookie and never wait; this
// only bounds the cold-client case where the middleware couldn't stash the flag
// and posthog-js must fetch it. Kept generous so a slow `/decide` on a cold load
// still routes fashion → Revolve instead of silently defaulting to Wayfair.
export const AFFILIATE_ROUTER_BROADCAST_READY_TIMEOUT_MS = 5000

/**
 * `affiliate-router` (experiment 382464, "Affiliate router — category-aware
 * 'Also search' (DUP-1553)") concluded 2026-07-23 at control 0 / test 100 —
 * the category-aware router won, so it stays hardcoded on (DUP-2451 flag
 * audit, 2026-09-17). The PostHog flag itself was found still ACTIVE and
 * still gated here eight weeks later, and is now an archive candidate. No
 * PostHog read, no bootstrap cookie, no readiness wait needed any more —
 * every caller downstream (the broadcast gate, the exposure capture, the
 * nested Ashley Furniture lane) already treats `arm` / `isReady` /
 * `routerEnabled` as opaque inputs, so this is the only site that changes.
 */
export default function useAffiliateRouterArm(): AffiliateRouterArmState {
  return {
    arm: 'test',
    isReady: true,
    routerEnabled: true,
  }
}

export function useAffiliateRouterBroadcastGate({
  eligible,
  isReady,
  timeoutMs = AFFILIATE_ROUTER_BROADCAST_READY_TIMEOUT_MS,
}: {
  eligible: boolean
  isReady: boolean
  timeoutMs?: number
}): AffiliateRouterBroadcastGate {
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!eligible) {
      setTimedOut(false)
      return undefined
    }

    if (isReady || timedOut) {
      return undefined
    }

    const timeout = window.setTimeout(() => setTimedOut(true), timeoutMs)
    return () => window.clearTimeout(timeout)
  }, [eligible, isReady, timedOut, timeoutMs])

  const effectiveTimedOut = eligible && !isReady && timedOut

  return {
    canBroadcast: eligible && (isReady || effectiveTimedOut),
    timedOut: effectiveTimedOut,
  }
}

export function useAffiliateRouterExposure({
  arm,
  eligible,
  surface,
}: {
  arm: AffiliateRouterArm | null
  eligible: boolean
  surface: string
}): void {
  const posthog = usePostHog()
  const exposedRef = useRef(false)

  useEffect(() => {
    if (!arm || !eligible || exposedRef.current) return

    exposedRef.current = true
    posthog.capture('affiliate_router_exposure', {
      variant: arm,
      surface,
    })
  }, [arm, eligible, posthog, surface])
}

export function useAffiliateRouterBroadcastWiring({
  eligible,
  surface,
}: {
  eligible: boolean
  surface: string
}): AffiliateRouterBroadcastWiring {
  const affiliateRouterArm = useAffiliateRouterArm()
  const ashleyFurnitureLane = useAshleyFurnitureLane()
  const broadcastGate = useAffiliateRouterBroadcastGate({
    eligible,
    isReady: affiliateRouterArm.isReady,
  })

  useAffiliateRouterExposure({
    arm: affiliateRouterArm.arm,
    eligible: eligible && affiliateRouterArm.isReady,
    surface,
  })

  // DUP-1920: the Ashley lane is NESTED inside this experiment — only the
  // router's `test` arm reaches the resolver, so a user outside it can never be
  // affected and must not enter Ashley's denominator. Gated on `routerEnabled`
  // for exactly that reason; without it the control arm fills with users who
  // were never eligible and any SRM check becomes meaningless.
  //
  // ⛔ ...and gated on `!ASHLEY_LANE_PAUSED` since 2026-08-18. A 0% flag rollout
  // does NOT stop exposures on its own — everyone simply resolves to `control`,
  // so this would keep firing on every eligible furniture query and bill a
  // PostHog denominator for an experiment that is no longer running.
  useAshleyFurnitureLaneExposure({
    arm: ashleyFurnitureLane.arm,
    eligible:
      !ASHLEY_LANE_PAUSED &&
      eligible &&
      affiliateRouterArm.isReady &&
      affiliateRouterArm.routerEnabled &&
      ashleyFurnitureLane.isReady,
    surface,
  })

  return {
    shouldBroadcast: broadcastGate.canBroadcast,
    routerArm: broadcastGate.timedOut ? null : affiliateRouterArm.arm,
    routerEnabled: !broadcastGate.timedOut && affiliateRouterArm.routerEnabled,
    arm: affiliateRouterArm.arm,
    isReady: affiliateRouterArm.isReady,
    timedOut: broadcastGate.timedOut,
  }
}
